import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Leftsidebar from "../sidebar/leftsidebar";
import LeftResponsivesidebar from "../sidebar/left-responsive";
import Headmenu from "../sidebar/headmenu";
import Loading from "../common/Loading";
import CreateAdSet from "./CreateAdSet";
import {
  getAdSet,
  getCampaign,
  getAdAccount,
  getAdSetTargeting,
  updateAdSet,
  createAdSetTargeting,
  updateAdSetTargeting,
} from "../../hook/useAdsApi";
import { AGE_MIN, AGE_MAX, formatInterestLabel } from "./adsConstants";

export default function EditAdSetPage() {
  const { adSetId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [adAccount, setAdAccount] = useState(null);
  const [formData, setFormData] = useState(null);
  // Tracks whether a targeting row already existed, so Save knows whether to PUT (update) or
  // POST (create) - per the API, POSTing when one already exists returns 409.
  const [targetingExists, setTargetingExists] = useState(false);

  useEffect(() => {
    let adSetResult;
    getAdSet(adSetId)
      .then((result) => {
        adSetResult = result;
        return Promise.all([
          getCampaign(result.campaign_id),
          getAdSetTargeting(adSetId).catch(() => null),
        ]);
      })
      .then(([campaignResult, targeting]) => {
        setTargetingExists(!!targeting);
        setFormData({
          adSetName: adSetResult.name || "",
          targetType: adSetResult.target_type || "profile",
          targetId: adSetResult.target_id || "",
          gender: targeting?.genders?.[0] || "all",
          minAge: targeting?.min_age ?? AGE_MIN,
          maxAge: targeting?.max_age ?? AGE_MAX,
          interests: targeting?.interests?.map((i) => ({ id: i.id, label: formatInterestLabel(i.category) })) || [],
          devices: targeting?.devices?.[0] || "all",
          connectToSeller: targeting?.seller_relationship?.followers || false,
          previousBrandBuyers: targeting?.previous_brand_buyers || false,
        });
        return getAdAccount(campaignResult.ad_account_id);
      })
      .then(setAdAccount)
      .catch(() => toast.error("Failed to load ad set", { toastId: "edit-ad-set-load-error" }))
      .finally(() => setLoading(false));
  }, [adSetId]);

  const updateFormData = (patch) => setFormData((prev) => ({ ...prev, ...patch }));

  const handleSave = async () => {
    setSubmitting(true);
    try {
      await updateAdSet(adSetId, {
        name: formData.adSetName,
        target_type: formData.targetType,
        target_id: formData.targetId || undefined,
      });

      const targetingPayload = {
        min_age: formData.minAge,
        max_age: formData.maxAge,
        genders: [formData.gender],
        ...(formData.devices !== "all" && { devices: [formData.devices] }),
        interest_ids: formData.interests.map((i) => i.id),
        seller_relationship: { followers: formData.connectToSeller },
        previous_brand_buyers: formData.previousBrandBuyers,
      };

      try {
        if (targetingExists) {
          await updateAdSetTargeting(adSetId, targetingPayload);
        } else {
          await createAdSetTargeting(adSetId, targetingPayload);
        }
      } catch (targetingError) {
        // The ad set's own fields already saved - don't block navigation over the targeting
        // save failing, just surface a warning, same pattern as the create flow.
        toast.warning(targetingError.response?.data?.message || "Failed to save ad set targeting");
      }

      toast.success("Ad set updated");
      navigate(`/ads/ad-sets/${adSetId}`);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update ad set");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="dashboard w-full min-h-screen flex">
      <Leftsidebar />
      <main className="flex-1 min-w-0 p-4 md:p-6 md:pl-8">
        <LeftResponsivesidebar />
        <Headmenu />
        <main className="flex">
          <div className="w-full max-w-2xl">
            <div className="flex items-center gap-3 mb-6">
              <button onClick={() => navigate(-1)} className="rounded-full bg-[#e7f3ea] p-2">
                ←
              </button>
              <h1 className="text-xl font-bold">Edit Ad Set</h1>
            </div>

            {loading || !formData ? (
              <div className="flex justify-center mt-16">
                <Loading />
              </div>
            ) : (
              <CreateAdSet
                formData={formData}
                updateFormData={updateFormData}
                onNext={handleSave}
                onBack={() => navigate(-1)}
                continueLabel="Save Changes"
                submitting={submitting}
                adAccount={adAccount}
              />
            )}
          </div>
        </main>
      </main>
    </div>
  );
}
